/**
 * Contexts — list view on the shared list grammar (design-system §4.2). The name
 * leads (the row links to detail), then the context type, the maturity badge
 * (black/neutral ladder), priority, and the lifecycle status via the shared
 * StatusBadge. The shared DataTable supplies the Loading / Empty / Error states.
 */
import * as React from "react";
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useGo, useNavigation } from "@refinedev/core";
import type { Context } from "@aos/shared";

import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/app/StatusBadge";

import { ResourceListPage, RowActions } from "../_shared/ListScaffold";
import { humanize } from "../projects/_form";
import { MaturityBadge } from "./MaturityBadge";

export function ContextList() {
  const go = useGo();
  const { editUrl, showUrl } = useNavigation();
  const [pendingDelete, setPendingDelete] = React.useState<Context | null>(
    null,
  );

  const columns = useMemo<ColumnDef<Context, unknown>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => (
          <span className="font-medium">{row.original.name}</span>
        ),
      },
      {
        id: "context_type",
        accessorKey: "context_type",
        header: "Type",
        cell: ({ row }) => (
          <Badge variant="outline">{humanize(row.original.context_type)}</Badge>
        ),
      },
      {
        id: "maturity_stage",
        accessorKey: "maturity_stage",
        header: "Maturity",
        cell: ({ row }) => <MaturityBadge stage={row.original.maturity_stage} />,
      },
      {
        id: "priority",
        accessorKey: "priority",
        header: "Priority",
        cell: ({ row }) => (
          <span className="tabular-nums text-muted-foreground">
            {row.original.priority}
          </span>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions
            onShow={() => go({ to: showUrl("contexts", row.original.id) })}
            onEdit={() => go({ to: editUrl("contexts", row.original.id) })}
            onDelete={() => setPendingDelete(row.original)}
          />
        ),
      },
    ],
    [go, editUrl, showUrl],
  );

  return (
    <ResourceListPage<Context>
      resource="contexts"
      title="Contexts"
      description="Operating contexts — fields, workflows, principles, and frames that shape agent behavior."
      createLabel="New Context"
      columns={columns}
      initialSort={[{ field: "priority", order: "desc" }]}
      searchPlaceholder="Search contexts…"
      emptyMessage="No contexts defined yet. Add one to start mapping where you operate."
      deleteEntityLabel="context"
      deleteName={(c) => c.name}
      pendingDelete={pendingDelete}
      setPendingDelete={setPendingDelete}
    />
  );
}

export default ContextList;
